<template>
  <div>
    <GenericError v-if="hasError" class="mt-48">
      There was an error creating your profile. All of your information has been saved. Refresh the page to try again or
      <a class="link-secondary underline" href="https://support.sittercity.com/hc/en-us/requests/new" target="_blank"
        >contact us</a
      >
      if the issue continues.
    </GenericError>
    <FormStepper
      v-else
      :steps="steps"
      progressBarTitle="Building profile"
      progressBarLastStepTitle="Last profile step!"
      :loading="loading"
      @submit="handleSubmit"
    >
      <template #progressBar="{ currentStep, maxSteps }">
        <div class="flex gap-16 mt-16">
          <ProgressBar :value="currentStep" :max="maxSteps" content="Building Profile" />
          <ProgressBar :value="0" :max="3" content="Verifying Identity" />
        </div>
      </template>
    </FormStepper>
  </div>
</template>

<script lang="ts">
import currency from 'currency.js';
import GenericError from '@/components/generic_error/GenericError.vue';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import FormStepper from '@/components/form_stepper/FormStepper.vue';
import YearsExperienceStep from './components/YearsExperienceStep.vue';
import AgeGroupsStep from './components/AgeGroupsStep.vue';
import SkillsStep from './components/SkillsStep.vue';
import QualificationsStep from './components/QualificationsStep.vue';
import DistanceStep from './components/DistanceStep.vue';
import PaymentRateFields from './components/PaymentRateFields.vue';
import BioArea from './components/BioArea.vue';
import { UPSERT_PROFILE_SITTER } from '@/graphql/profile/mutations';
import { redirect } from '@/helpers/redirects';
import { skillsToggleTokenOptions } from './components/__types__/SkillTypes';
import { sendSegmentEvent } from '@/lib/segment';

export default {
  name: 'ProfileSteps',
  components: { GenericError, ProgressBar, FormStepper },
  data(): any {
    return {
      careType: this.$store.getters.careType,
      loading: false,
      hasError: false,
      steps: [
        {
          title: 'How many years of paid child care experience do you have?',
          name: 'Experience',
          required: true,
          component: YearsExperienceStep,
          alwaysFullWidthButton: true,
          transitionText: 'Nice start',
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Experience Entered', {
              years_experience: this.$store.getters.getFormField('yearsExperience'),
            });
          },
          previousStepCallback: () => {},
        },
        {
          name: 'Ages',
          title: 'What ages do you have experience with?',
          required: true,
          component: AgeGroupsStep,
          transitionText: 'Looks good',
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Ages of Experience Entered', {
              ages_experience: this.$store.getters.getFormField('ageGroups'),
            });
          },
          previousStepCallback: () => sendSegmentEvent('Profile - Prior Page Selected', { link_text: 'Experience' }),
        },
        {
          name: 'Skills',
          title: 'Add your skills and services',
          component: SkillsStep,
          xlCard: true,
          transitionText: 'Families will love',
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Skills and Services Selected', {
              skills_services: skillsToggleTokenOptions
                .map((option) => option.type)
                .filter((service) => this.$store.getters.getFormField(service)),
            });
          },
          previousStepCallback: () => sendSegmentEvent('Profile - Prior Page Selected', { link_text: 'Ages' }),
        },
        {
          name: 'Certifications',
          title: 'Do you have any training or certifications?',
          component: QualificationsStep,
          transitionText: 'Thanks',
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Certifications Entered', {
              certifications: this.$store.getters.getFormField('qualifications'),
            });
          },
          previousStepCallback: () => sendSegmentEvent('Profile - Prior Page Selected', { link_text: 'Skills' }),
        },
        {
          name: 'Distance',
          title: "What is the max distance you'll travel for a job?",
          required: true,
          component: DistanceStep,
          transitionText: 'Cool',
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Travel Distance Entered', {
              max_distance: this.$store.getters.getFormField('distanceWillingToTravel'),
            });
          },
          previousStepCallback: () => sendSegmentEvent('Profile - Prior Page Selected', { link_text: 'Certifications' }),
        },
        {
          name: 'Rate',
          title: 'Set your rate',
          required: true,
          component: PaymentRateFields,
          transitionText: "That's perfect",
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Rate Set', {
              rate_min: this.$store.getters.minPaymentRate,
              rate_max: this.$store.getters.maxPaymentRate,
            });
          },
          previousStepCallback: () => sendSegmentEvent('Profile - Prior Page Selected', { link_text: 'Distance' }),
        },
        {
          name: 'Bio',
          title: 'Write a bio',
          required: true,
          component: BioArea,
          transitionText: 'Great work!',
          nextStepCallback: () => {
            sendSegmentEvent('Profile - Bio Written', {
              bio_text: this.$store.getters.getFormField('bio'),
            });
          },
          previousStepCallback: () => sendSegmentEvent('Profile - Prior Page Selected', { link_text: 'Rate' }),
        },
      ],
    };
  },
  computed: {
    profileInput(): object {
      const fields = [
        'yearsExperience',
        'ageGroups',
        'accessToTransportation',
        'lightHousekeeping',
        'willingToDriveChildren',
        'willingToRunErrands',
        'homeworkHelp',
        'laundry',
        'mealPreparation',
        'comfortableWithPets',
        'pottyTraining',
        'sickChildren',
        'specialNeeds',
        'twins',
        'qualifications',
        'distanceWillingToTravel',
        'paymentRate',
        'bio',
      ];

      const result: any = {};

      fields.forEach((field) => {
        result[field] = this.$store.getters.getFormField(field);
      });

      // Handling specific fields with transformation
      result.paymentRate = {
        min: currency(this.$store.getters.minPaymentRate).intValue,
        max: currency(this.$store.getters.maxPaymentRate).intValue,
      };

      return result;
    },
  },
  methods: {
    async handleSubmit() {
      this.loading = true;
      try {
        await this.$apollo.mutate({
          mutation: UPSERT_PROFILE_SITTER,
          variables: {
            profile: {
              [this.careType]: this.profileInput,
            },
          },
        });
        sendSegmentEvent('Profile - Public Profile Completed', {});
        this.redirectToVerifyIdentity();
      } catch (error) {
        this.loading = false;
        this.hasError = true;
        console.log(error); // eslint-disable-line no-console
      }
    },
    redirectToVerifyIdentity() {
      redirect('/sitter/verify_identity');
    },
    sendPreviousStepEvent() {
      sendSegmentEvent('Profile - Prior Page Selected', {});
    },
  },
};
</script>
